import {
  createContext,
  useContext,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useAuth } from '@clerk/react';
import { useLocation } from 'wouter';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PendingEntry {
  marketId: string;
  marketQuestion: string;
}

interface AnalysisNotificationContextValue {
  registerAnalysis: (
    analysisId: number,
    marketId: string,
    marketQuestion: string,
  ) => void;
}

// ─── localStorage persistence ─────────────────────────────────────────────────

const LS_KEY = 'mm:pending-analyses';

function loadFromStorage(): Map<number, PendingEntry> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return new Map();
    const arr = JSON.parse(raw) as [number, PendingEntry][];
    return new Map(arr);
  } catch {
    return new Map();
  }
}

function saveToStorage(map: Map<number, PendingEntry>) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(Array.from(map.entries())));
  } catch {
    // ignore quota errors
  }
}

function loadNotifiedFromStorage(): Set<number> {
  try {
    const raw = localStorage.getItem(`${LS_KEY}:notified`);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as number[]);
  } catch {
    return new Set();
  }
}

function saveNotifiedToStorage(set: Set<number>) {
  try {
    // Only keep the last 50 to avoid unbounded growth
    const arr = Array.from(set).slice(-50);
    localStorage.setItem(`${LS_KEY}:notified`, JSON.stringify(arr));
  } catch {}
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AnalysisNotificationContext =
  createContext<AnalysisNotificationContextValue>({
    registerAnalysis: () => {},
  });

export function useAnalysisNotifications() {
  return useContext(AnalysisNotificationContext);
}

// ─── Provider ─────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 2_000;

export function AnalysisNotificationProvider({
  children,
}: {
  children: ReactNode;
}) {
  // Restored from localStorage so pending analyses survive page refreshes.
  const pendingRef = useRef<Map<number, PendingEntry>>(loadFromStorage());
  const notifiedRef = useRef<Set<number>>(loadNotifiedFromStorage());

  const { isSignedIn } = useAuth();
  const [, navigate] = useLocation();

  // Kept in a ref so the async poll continuation can guard itself after each
  // await — React state/props are not safe to read inside a stale closure.
  const isSignedInRef = useRef<boolean | undefined>(isSignedIn);
  useEffect(() => {
    isSignedInRef.current = isSignedIn;
  }, [isSignedIn]);

  // Clear all notification state when the user signs out so a new user on the
  // same browser never sees toasts from the previous session.
  useEffect(() => {
    if (isSignedIn === false) {
      isSignedInRef.current = false;
      pendingRef.current.clear();
      notifiedRef.current.clear();
      localStorage.removeItem(LS_KEY);
      localStorage.removeItem(`${LS_KEY}:notified`);
    }
  }, [isSignedIn]);

  const registerAnalysis = useCallback(
    (analysisId: number, marketId: string, marketQuestion: string) => {
      if (notifiedRef.current.has(analysisId)) return;
      if (pendingRef.current.has(analysisId)) return;
      pendingRef.current.set(analysisId, { marketId, marketQuestion });
      saveToStorage(pendingRef.current);
    },
    [],
  );

  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  useEffect(() => {
    let mounted = true;

    const interval = setInterval(async () => {
      const entries = Array.from(pendingRef.current.entries());
      if (entries.length === 0) return;

      await Promise.all(
        entries.map(async ([analysisId, info]) => {
          try {
            const res = await fetch(`/api/analyses/${analysisId}`, {
              credentials: 'include',
            });

            // Bail out if the provider unmounted while the request was in-flight.
            if (!mounted) return;

            // Guard: sign-out may have fired while the request was in-flight.
            // Bail out immediately so we never mutate refs or show a toast for
            // data that belongs to the previous user's session.
            if (!isSignedInRef.current) return;

            if (!res.ok) {
              // 404 means the analysis was deleted — stop tracking it
              if (res.status === 404) {
                pendingRef.current.delete(analysisId);
                saveToStorage(pendingRef.current);
              }
              return;
            }

            const analysis = (await res.json()) as {
              status: string;
              recommendation?: string;
            };

            // Re-check both guards after the second await (res.json()).
            if (!mounted) return;
            if (!isSignedInRef.current) return;

            if (analysis.status === 'complete' || analysis.status === 'failed') {
              pendingRef.current.delete(analysisId);
              saveToStorage(pendingRef.current);

              if (
                analysis.status === 'complete' &&
                !notifiedRef.current.has(analysisId)
              ) {
                notifiedRef.current.add(analysisId);
                saveNotifiedToStorage(notifiedRef.current);

                const rec = analysis.recommendation ?? 'N/A';
                const question =
                  info.marketQuestion.length > 70
                    ? info.marketQuestion.slice(0, 70) + '…'
                    : info.marketQuestion;

                const recLabel =
                  rec === 'YES' ? '✅ YES' : rec === 'NO' ? '❌ NO' : rec;

                toast({
                  title: `✨ Analysis ready — ${recLabel}`,
                  description: question,
                  duration: 10_000,
                  action: (
                    <ToastAction
                      altText="View analysis"
                      onClick={() =>
                        navigateRef.current(`/markets/${info.marketId}`)
                      }
                    >
                      View
                    </ToastAction>
                  ),
                });
              }
            }
          } catch {
            // Network errors are transient; keep polling.
          }
        }),
      );
    }, POLL_INTERVAL_MS);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  return (
    <AnalysisNotificationContext.Provider value={{ registerAnalysis }}>
      {children}
    </AnalysisNotificationContext.Provider>
  );
}
